import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import TopBar from '../components/layout/TopBar';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { itemStatus, statusStyle } from './TrailersPage';

const DOC_TYPES = [
  { val: 'registration', label: 'Registration' },
  { val: 'insurance', label: 'Insurance' },
  { val: 'warranty', label: 'Warranty' },
  { val: 'inspection', label: 'Inspection' },
  { val: 'service_record', label: 'Service / maintenance record' },
  { val: 'receipt_invoice', label: 'Receipt / invoice' },
  { val: 'manual', label: 'Manual' },
  { val: 'title_ownership', label: 'Title / ownership' },
  { val: 'other', label: 'Other' },
];

const buildDocForm = () => ({
  title: '',
  document_type: 'registration',
  document_date: '',
  issued_by: '',
  notes: '',
  has_expiry: false,
  expiry_date: '',
  enable_expiry_reminder: true,
  expiry_reminder_days: '30',
});

const fmtDate = (d) => {
  if (!d) return '';
  return new Date(d + (String(d).length === 10 ? 'T00:00:00' : ''))
    .toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
};

const daysUntil = (d) => {
  if (!d) return null;
  const due = new Date(d + 'T00:00:00');
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.ceil((due.getTime() - now.getTime()) / 86400000);
};

const ExpiryBadge = ({ date }) => {
  if (!date) return null;
  const days = daysUntil(date);

  if (days < 0) {
    return <span className="badge badge-red">Expired</span>;
  }

  if (days <= 30) {
    return <span className="badge badge-amber">{days}d left</span>;
  }

  return <span className="badge badge-green">Current</span>;
};

const TrailerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const organizationId =
    searchParams.get('org') ||
    searchParams.get('organization_id') ||
    '';

  const orgQuery = organizationId
    ? `?organization_id=${encodeURIComponent(organizationId)}`
    : '';

  const backPath = '/trailers' + (
    organizationId
      ? `?org=${encodeURIComponent(organizationId)}`
      : ''
  );

  const [trailer, setTrailer] = useState(null);
  const [log, setLog] = useState([]);
  const [documents, setDocuments] = useState([]);

  const [doneId, setDoneId] = useState(null);
  const [doneForm, setDoneForm] = useState({
    done_date: new Date().toISOString().slice(0, 10),
    notes: '',
    cost: ''
  });

  const [newItem, setNewItem] = useState({
    label: '',
    interval_months: 12
  });

  const [addingItem, setAddingItem] = useState(false);

  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [docForm, setDocForm] = useState(buildDocForm());
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [r, lr, dr] = await Promise.all([
        api.get('/trailers' + orgQuery),
        api.get('/trailers/' + id + '/log'),
        api.get('/trailers/' + id + '/documents')
      ]);

      const t = (r.data || []).find(x => x.id === id);

      if (!t) {
        toast.error('Trailer not found');
        navigate(backPath);
        return;
      }

      setTrailer(t);
      setLog(lr.data || []);
      setDocuments(dr.data || []);
    } catch (e) {
      toast.error('Failed to load');
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, organizationId]);

  const markDone = async (item) => {
    try {
      await api.post(
        '/trailers/items/' + item.id + '/done',
        doneForm
      );

      toast.success(item.label + ' logged');
      setDoneId(null);

      setDoneForm({
        done_date: new Date().toISOString().slice(0, 10),
        notes: '',
        cost: ''
      });

      load();
    } catch (e) {
      toast.error('Failed to log');
    }
  };

  const addItem = async () => {
    if (!newItem.label.trim()) {
      return toast.error('Name the maintenance item');
    }

    try {
      await api.post(
        '/trailers/' + id + '/items',
        newItem
      );

      setNewItem({
        label: '',
        interval_months: 12
      });

      setAddingItem(false);
      load();
    } catch (e) {
      toast.error('Failed to add item');
    }
  };

  const removeItem = async (item) => {
    if (
      !window.confirm(
        'Stop tracking "' +
        item.label +
        '"? Its past log entries are kept.'
      )
    ) return;

    try {
      await api.put(
        '/trailers/items/' + item.id,
        { is_active: false }
      );

      load();
    } catch (e) {
      toast.error('Failed');
    }
  };

  const deleteTrailer = async () => {
    if (
      !window.confirm(
        'Remove ' +
        trailer.name +
        ' and its entire maintenance and document history? This cannot be undone.'
      )
    ) return;

    try {
      await api.delete('/trailers/' + id);
      toast.success('Trailer removed');
      navigate(backPath);
    } catch (e) {
      toast.error('Failed');
    }
  };

  const openUpload = () => {
    setDocForm(buildDocForm());
    setUploadFile(null);

    if (fileRef.current) {
      fileRef.current.value = '';
    }

    setShowUpload(true);
  };

  const closeUpload = () => {
    setShowUpload(false);
    setUploadFile(null);
    setDocForm(buildDocForm());

    if (fileRef.current) {
      fileRef.current.value = '';
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    setUploadFile(file);

    if (!docForm.title) {
      setDocForm(p => ({
        ...p,
        title: file.name.replace(/\.[^/.]+$/, '')
      }));
    }
  };

  const uploadDocument = async () => {
    if (!uploadFile) {
      return toast.error('Choose a file');
    }

    if (!docForm.title.trim()) {
      return toast.error('Document title required');
    }

    if (!docForm.document_type) {
      return toast.error('Choose a document type');
    }

    if (docForm.has_expiry && !docForm.expiry_date) {
      return toast.error('Enter the expiration or renewal date');
    }

    setUploading(true);

    try {
      const fd = new FormData();

      fd.append('file', uploadFile);
      fd.append('title', docForm.title.trim());
      fd.append('document_type', docForm.document_type);

      if (docForm.document_date) {
        fd.append('document_date', docForm.document_date);
      }

      if (docForm.issued_by.trim()) {
        fd.append('issued_by', docForm.issued_by.trim());
      }

      if (docForm.notes.trim()) {
        fd.append('notes', docForm.notes.trim());
      }

      if (docForm.has_expiry) {
        fd.append('expiry_date', docForm.expiry_date);

        fd.append(
          'enable_expiry_reminder',
          docForm.enable_expiry_reminder ? 'true' : 'false'
        );

        if (docForm.enable_expiry_reminder) {
          fd.append(
            'expiry_reminder_days',
            docForm.expiry_reminder_days
          );
        }
      } else {
        fd.append('enable_expiry_reminder', 'false');
      }

      await api.post(
        '/trailers/' + id + '/documents',
        fd,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('Document uploaded');
      closeUpload();
      load();
    } catch (e) {
      toast.error(
        (e.response &&
          e.response.data &&
          e.response.data.message) ||
        'Upload failed'
      );
    } finally {
      setUploading(false);
    }
  };

  const viewDocument = async (doc) => {
    const tab = window.open('', '_blank');

    try {
      const { data } = await api.get(
        '/trailers/' +
        id +
        '/documents/' +
        doc.id +
        '/file',
        {
          responseType: 'blob'
        }
      );

      const url = URL.createObjectURL(data);

      if (tab) {
        tab.location.href = url;
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noreferrer';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }

      setTimeout(
        () => URL.revokeObjectURL(url),
        60000
      );
    } catch (e) {
      if (tab) tab.close();
      toast.error('Failed to open document');
    }
  };

  const deleteDocument = async (doc) => {
    if (
      !window.confirm(
        'Delete "' +
        doc.title +
        '"? This removes the uploaded file and any reminder created for it.'
      )
    ) return;

    try {
      await api.delete(
        '/trailers/' +
        id +
        '/documents/' +
        doc.id
      );

      toast.success('Document deleted');
      load();
    } catch (e) {
      toast.error('Failed to delete document');
    }
  };

  if (!trailer) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
      </div>
    );
  }

  const rank = {
    overdue: 0,
    soon: 1,
    never: 2,
    good: 3
  };

  const items = [...trailer.items].sort(
    (a, b) =>
      rank[itemStatus(a).key] -
      rank[itemStatus(b).key]
  );

  return (
    <div>
      <TopBar
        title={trailer.name}
        subtitle={
          [
            trailer.year,
            trailer.make,
            (trailer.trailer_type || '').replace('_', ' ')
          ]
            .filter(Boolean)
            .join(' ') || 'Trailer'
        }
      />

      <div className="page-content">
        <div className="section-label">
          Maintenance items
        </div>

        {items.map(item => {
          const st = itemStatus(item);

          return (
            <div
              key={item.id}
              className="card"
              style={{
                padding: '10px 14px'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600
                    }}
                  >
                    {item.label}
                  </div>

                  <div
                    style={{
                      fontSize: 11,
                      color: 'var(--text-muted)'
                    }}
                  >
                    Every {item.interval_months} mo
                    {item.last_done_date
                      ? ' · last done ' +
                        new Date(
                          item.last_done_date +
                          'T00:00:00'
                        ).toLocaleDateString(
                          'en-US',
                          {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          }
                        )
                      : ''}
                  </div>
                </div>

                <span
                  className="badge"
                  style={{
                    fontSize: 10,
                    padding: '3px 8px',
                    borderRadius: 8,
                    ...statusStyle(st.key)
                  }}
                >
                  {st.label}
                </span>
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: 6,
                  marginTop: 8
                }}
              >
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    setDoneId(
                      doneId === item.id
                        ? null
                        : item.id
                    );
                  }}
                >
                  Mark done
                </button>

                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => removeItem(item)}
                >
                  Remove
                </button>
              </div>

              {doneId === item.id && (
                <div
                  style={{
                    marginTop: 8,
                    padding: 10,
                    border:
                      '1px solid var(--border-color)',
                    borderRadius: 10
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      gap: 8
                    }}
                  >
                    <input
                      type="date"
                      className="form-input"
                      value={doneForm.done_date}
                      onChange={e =>
                        setDoneForm(f => ({
                          ...f,
                          done_date:
                            e.target.value
                        }))
                      }
                    />

                    <input
                      className="form-input"
                      placeholder="Cost (optional)"
                      value={doneForm.cost}
                      onChange={e =>
                        setDoneForm(f => ({
                          ...f,
                          cost: e.target.value
                        }))
                      }
                    />
                  </div>

                  <input
                    className="form-input"
                    style={{ marginTop: 6 }}
                    placeholder="Notes — who did it, parts, findings"
                    value={doneForm.notes}
                    onChange={e =>
                      setDoneForm(f => ({
                        ...f,
                        notes: e.target.value
                      }))
                    }
                  />

                  <button
                    className="btn btn-primary btn-sm"
                    style={{ marginTop: 6 }}
                    onClick={() =>
                      markDone(item)
                    }
                  >
                    Save to log
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {!addingItem && (
          <button
            className="btn btn-secondary btn-full"
            onClick={() =>
              setAddingItem(true)
            }
          >
            + Add maintenance item
          </button>
        )}

        {addingItem && (
          <div className="card">
            <div
              style={{
                display: 'flex',
                gap: 8
              }}
            >
              <input
                className="form-input"
                style={{ flex: 2 }}
                placeholder="e.g. Generator oil change"
                value={newItem.label}
                onChange={e =>
                  setNewItem(f => ({
                    ...f,
                    label: e.target.value
                  }))
                }
              />

              <select
                className="form-input"
                style={{ flex: 1 }}
                value={
                  newItem.interval_months
                }
                onChange={e =>
                  setNewItem(f => ({
                    ...f,
                    interval_months:
                      parseInt(
                        e.target.value,
                        10
                      )
                  }))
                }
              >
                {[1, 3, 6, 12, 24].map(
                  m => (
                    <option
                      key={m}
                      value={m}
                    >
                      Every {m} mo
                    </option>
                  )
                )}
              </select>
            </div>

            <div
              style={{
                display: 'flex',
                gap: 8,
                marginTop: 8
              }}
            >
              <button
                className="btn btn-primary btn-sm"
                onClick={addItem}
              >
                Add
              </button>

              <button
                className="btn btn-secondary btn-sm"
                onClick={() =>
                  setAddingItem(false)
                }
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div
          className="section-label"
          style={{ marginTop: 16 }}
        >
          Documents &amp; records
        </div>

        {documents.length === 0 && (
          <div
            className="card"
            style={{
              padding: '12px 14px'
            }}
          >
            <div
              style={{
                fontSize: 13,
                fontWeight: 600
              }}
            >
              No documents uploaded yet
            </div>

            <div
              style={{
                fontSize: 11,
                color: 'var(--text-muted)',
                marginTop: 3
              }}
            >
              Keep registrations, warranties,
              service records, receipts and other
              trailer paperwork here.
            </div>
          </div>
        )}

        {documents.map(doc => {
          const type =
            DOC_TYPES.find(
              t => t.val === doc.document_type
            );

          const meta = [
            type
              ? type.label
              : doc.document_type,
            doc.issued_by || null,
            doc.document_date
              ? 'Dated ' +
                fmtDate(doc.document_date)
              : null,
            doc.expiry_date
              ? 'Renews / expires ' +
                fmtDate(doc.expiry_date)
              : null
          ]
            .filter(Boolean)
            .join(' · ');

          return (
            <div
              key={doc.id}
              className="card"
              style={{
                padding: '10px 14px'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems:
                    'flex-start',
                  gap: 10
                }}
              >
                <div
                  style={{
                    fontSize: 18,
                    lineHeight: 1
                  }}
                >
                  📄
                </div>

                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600
                    }}
                  >
                    {doc.title}
                  </div>

                  <div
                    style={{
                      fontSize: 11,
                      color:
                        'var(--text-muted)',
                      marginTop: 2
                    }}
                  >
                    {meta}
                  </div>

                  {doc.expiry_date &&
                    doc.expiry_reminder_days && (
                      <div
                        style={{
                          fontSize: 11,
                          color:
                            'var(--text-secondary)',
                          marginTop: 2
                        }}
                      >
                        Reminder set{' '}
                        {
                          doc.expiry_reminder_days
                        }{' '}
                        days before
                      </div>
                    )}

                  {doc.notes && (
                    <div
                      style={{
                        fontSize: 11,
                        color:
                          'var(--text-secondary)',
                        marginTop: 4
                      }}
                    >
                      {doc.notes}
                    </div>
                  )}
                </div>

                <ExpiryBadge
                  date={doc.expiry_date}
                />
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: 6,
                  marginTop: 8
                }}
              >
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() =>
                    viewDocument(doc)
                  }
                >
                  View
                </button>

                <button
                  className="btn btn-danger btn-sm"
                  onClick={() =>
                    deleteDocument(doc)
                  }
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}

        <button
          className="btn btn-secondary btn-full"
          onClick={openUpload}
        >
          + Upload document
        </button>

        <div
          className="section-label"
          style={{ marginTop: 16 }}
        >
          Service log
        </div>

        {log.length === 0 && (
          <div
            style={{
              fontSize: 12,
              color: 'var(--text-muted)'
            }}
          >
            Nothing logged yet — every
            "Mark done" lands here permanently.
          </div>
        )}

        {log.map(e => (
          <div
            key={e.id}
            className="card"
            style={{
              padding: '10px 14px'
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent:
                  'space-between',
                fontSize: 13
              }}
            >
              <b>{e.label_snapshot}</b>

              <span>
                {new Date(
                  e.done_date +
                  'T00:00:00'
                ).toLocaleDateString(
                  'en-US',
                  {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  }
                )}
              </span>
            </div>

            {(e.notes || e.cost) && (
              <div
                style={{
                  fontSize: 12,
                  color:
                    'var(--text-secondary)',
                  marginTop: 2
                }}
              >
                {e.notes}
                {e.cost
                  ? (e.notes ? ' · ' : '') +
                    '$' +
                    e.cost
                  : ''}
              </div>
            )}
          </div>
        ))}

        <button
          className="btn btn-secondary btn-full"
          style={{
            marginTop: 16,
            color: '#8C2B22'
          }}
          onClick={deleteTrailer}
        >
          Remove this trailer
        </button>
      </div>

      {showUpload && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background:
              'rgba(0,0,0,0.5)',
            zIndex: 999,
            display: 'flex',
            alignItems: 'flex-end'
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius:
                '20px 20px 0 0',
              padding: 20,
              width: '100%',
              maxWidth: 480,
              margin: '0 auto',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent:
                  'space-between',
                marginBottom: 16
              }}
            >
              <h2>
                Upload trailer document
              </h2>

              <button
                onClick={closeUpload}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 22,
                  cursor: 'pointer'
                }}
              >
                ×
              </button>
            </div>

            <div
              onClick={() =>
                fileRef.current &&
                fileRef.current.click()
              }
              style={{
                border:
                  '2px dashed var(--border-strong)',
                borderRadius:
                  'var(--radius-lg)',
                padding: '20px',
                textAlign: 'center',
                cursor: 'pointer',
                marginBottom: 14,
                background: uploadFile
                  ? 'var(--cr-green-light)'
                  : 'var(--bg-secondary)'
              }}
            >
              {uploadFile ? (
                <>
                  <div
                    style={{
                      fontSize: 24
                    }}
                  >
                    ✅
                  </div>

                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      marginTop: 6
                    }}
                  >
                    {uploadFile.name}
                  </div>

                  <div
                    style={{
                      fontSize: 11,
                      color:
                        'var(--text-muted)'
                    }}
                  >
                    {(
                      uploadFile.size /
                      1024
                    ).toFixed(0)}{' '}
                    KB · tap to change
                  </div>
                </>
              ) : (
                <>
                  <div
                    style={{
                      fontSize: 32
                    }}
                  >
                    📎
                  </div>

                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      marginTop: 8
                    }}
                  >
                    Tap to choose file
                  </div>

                  <div
                    style={{
                      fontSize: 11,
                      color:
                        'var(--text-muted)',
                      marginTop: 4
                    }}
                  >
                    PDF, Word document,
                    JPG, PNG, WEBP or
                    iPhone image — up to
                    10MB
                  </div>
                </>
              )}
            </div>

            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.heic,.heif,image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />

            <div className="form-group">
              <label className="form-label">
                Document title *
              </label>

              <input
                className="form-input"
                placeholder="e.g. 2026 Trailer Registration"
                value={docForm.title}
                onChange={e =>
                  setDocForm(p => ({
                    ...p,
                    title: e.target.value
                  }))
                }
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Document type *
              </label>

              <select
                className="form-input"
                value={
                  docForm.document_type
                }
                onChange={e =>
                  setDocForm(p => ({
                    ...p,
                    document_type:
                      e.target.value
                  }))
                }
              >
                {DOC_TYPES.map(type => (
                  <option
                    key={type.val}
                    value={type.val}
                  >
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns:
                  '1fr 1fr',
                gap: 10
              }}
            >
              <div className="form-group">
                <label className="form-label">
                  Document date
                </label>

                <input
                  className="form-input"
                  type="date"
                  value={
                    docForm.document_date
                  }
                  onChange={e =>
                    setDocForm(p => ({
                      ...p,
                      document_date:
                        e.target.value
                    }))
                  }
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Issued by
                </label>

                <input
                  className="form-input"
                  placeholder="DMV, insurer, repair shop"
                  value={
                    docForm.issued_by
                  }
                  onChange={e =>
                    setDocForm(p => ({
                      ...p,
                      issued_by:
                        e.target.value
                    }))
                  }
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                Notes
              </label>

              <textarea
                className="form-input"
                rows="3"
                placeholder="Optional details"
                value={docForm.notes}
                onChange={e =>
                  setDocForm(p => ({
                    ...p,
                    notes: e.target.value
                  }))
                }
              />
            </div>

            <div
              className="card"
              style={{
                padding: 12,
                marginBottom: 14,
                background:
                  'var(--bg-secondary)'
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 8
                }}
              >
                Does this document expire
                or need renewal?
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: 8
                }}
              >
                <button
                  type="button"
                  className={
                    'btn btn-sm ' +
                    (!docForm.has_expiry
                      ? 'btn-primary'
                      : 'btn-secondary')
                  }
                  onClick={() =>
                    setDocForm(p => ({
                      ...p,
                      has_expiry: false,
                      expiry_date: ''
                    }))
                  }
                >
                  No
                </button>

                <button
                  type="button"
                  className={
                    'btn btn-sm ' +
                    (docForm.has_expiry
                      ? 'btn-primary'
                      : 'btn-secondary')
                  }
                  onClick={() =>
                    setDocForm(p => ({
                      ...p,
                      has_expiry: true
                    }))
                  }
                >
                  Yes
                </button>
              </div>

              {docForm.has_expiry && (
                <>
                  <div
                    className="form-group"
                    style={{
                      marginTop: 12
                    }}
                  >
                    <label className="form-label">
                      Expiration / renewal
                      date *
                    </label>

                    <input
                      className="form-input"
                      type="date"
                      value={
                        docForm.expiry_date
                      }
                      onChange={e =>
                        setDocForm(p => ({
                          ...p,
                          expiry_date:
                            e.target.value
                        }))
                      }
                    />
                  </div>

                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      marginBottom: 8
                    }}
                  >
                    Would you like a
                    reminder?
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      gap: 8,
                      marginBottom:
                        docForm.enable_expiry_reminder
                          ? 10
                          : 0
                    }}
                  >
                    <button
                      type="button"
                      className={
                        'btn btn-sm ' +
                        (!docForm.enable_expiry_reminder
                          ? 'btn-primary'
                          : 'btn-secondary')
                      }
                      onClick={() =>
                        setDocForm(p => ({
                          ...p,
                          enable_expiry_reminder:
                            false
                        }))
                      }
                    >
                      No
                    </button>

                    <button
                      type="button"
                      className={
                        'btn btn-sm ' +
                        (docForm.enable_expiry_reminder
                          ? 'btn-primary'
                          : 'btn-secondary')
                      }
                      onClick={() =>
                        setDocForm(p => ({
                          ...p,
                          enable_expiry_reminder:
                            true
                        }))
                      }
                    >
                      Yes
                    </button>
                  </div>

                  {docForm.enable_expiry_reminder && (
                    <div
                      className="form-group"
                      style={{
                        marginBottom: 0
                      }}
                    >
                      <label className="form-label">
                        Remind me
                      </label>

                      <select
                        className="form-input"
                        value={
                          docForm.expiry_reminder_days
                        }
                        onChange={e =>
                          setDocForm(p => ({
                            ...p,
                            expiry_reminder_days:
                              e.target.value
                          }))
                        }
                      >
                        <option value="7">
                          7 days before
                        </option>

                        <option value="14">
                          14 days before
                        </option>

                        <option value="30">
                          30 days before
                        </option>

                        <option value="60">
                          60 days before
                        </option>

                        <option value="90">
                          90 days before
                        </option>
                      </select>
                    </div>
                  )}
                </>
              )}
            </div>

            <button
              className="btn btn-primary btn-full"
              onClick={uploadDocument}
              disabled={
                uploading ||
                !uploadFile
              }
            >
              {uploading
                ? 'Uploading...'
                : 'Upload document'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrailerDetailPage;
